// src/components/Documentation/components/codeExamples.js

// Use environment variable for API URL, fallback to production URL
const baseURL = process.env.REACT_APP_API_URL || 'https://api.snaptura.online';
const apiKey = 'your_api_key_here';

export const codeExamples = {
  // Authentication Examples
  auth: {
    curl: `curl ${baseURL}/api/screenshot \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`,

    node: `const axios = require('axios');

const response = await axios.post('${baseURL}/api/screenshot', 
  { url: 'https://example.com' },
  { headers: { 'X-API-Key': '${apiKey}' } }
);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={'url': 'https://example.com'},
    headers={'X-API-Key': '${apiKey}'}
)`,

    php: `<?php
$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'X-API-Key: ${apiKey}',
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'url' => 'https://example.com'
]));
$response = curl_exec($ch);
curl_close($ch);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = { url: 'https://example.com' }.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "net/http"
)

func main() {
    body, _ := json.Marshal(map[string]string{
        "url": "https://example.com",
    })
    
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
}`
  },

  // Basic Screenshot
  basic: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{"url": "https://example.com"}' \\
  --output screenshot.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  { url: 'https://example.com' },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('screenshot.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={'url': 'https://example.com'},
    headers={'X-API-Key': '${apiKey}'}
)

with open('screenshot.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode(['url' => 'https://example.com']),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('screenshot.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = { url: 'https://example.com' }.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('screenshot.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    body, _ := json.Marshal(map[string]string{"url": "https://example.com"})
    
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("screenshot.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Full Page Screenshot
  fullPage: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "fullPage": true,
    "format": "webp",
    "quality": 85
  }' \\
  --output screenshot.webp`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://example.com',
    fullPage: true,
    format: 'webp',
    quality: 85
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('screenshot.webp', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://example.com',
        'fullPage': True,
        'format': 'webp',
        'quality': 85
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('screenshot.webp', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'fullPage' => true,
    'format' => 'webp',
    'quality' => 85
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('screenshot.webp', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  fullPage: true,
  format: 'webp',
  quality: 85
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('screenshot.webp', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "url":      "https://example.com",
        "fullPage": true,
        "format":   "webp",
        "quality":  85,
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("screenshot.webp")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Block Ads and Cookie Banners
  blocking: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://cnn.com",
    "blockCookieBanners": true,
    "blockAds": true
  }' \\
  --output clean.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://cnn.com',
    blockCookieBanners: true,
    blockAds: true
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('clean.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://cnn.com',
        'blockCookieBanners': True,
        'blockAds': True
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('clean.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://cnn.com',
    'blockCookieBanners' => true,
    'blockAds' => true
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('clean.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://cnn.com',
  blockCookieBanners: true,
  blockAds: true
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('clean.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "url":                "https://cnn.com",
        "blockCookieBanners": true,
        "blockAds":           true,
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("clean.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Device Emulation
  device: {
    curl: `curl ${baseURL}/api/screenshot/device \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "device": "iPhone 12"
  }' \\
  --output mobile.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot/device',
  {
    url: 'https://example.com',
    device: 'iPhone 12'
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('mobile.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot/device',
    json={
        'url': 'https://example.com',
        'device': 'iPhone 12'
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('mobile.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'device' => 'iPhone 12'
];

$ch = curl_init('${baseURL}/api/screenshot/device');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('mobile.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot/device')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  device: 'iPhone 12'
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('mobile.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]string{
        "url":    "https://example.com",
        "device": "iPhone 12",
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot/device", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("mobile.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Custom CSS
  customCSS: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "customCSS": "body { background: #f0f0f0; } .header { display: none; }"
  }' \\
  --output styled.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://example.com',
    customCSS: 'body { background: #f0f0f0; } .header { display: none; }'
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('styled.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://example.com',
        'customCSS': 'body { background: #f0f0f0; } .header { display: none; }'
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('styled.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'customCSS' => 'body { background: #f0f0f0; } .header { display: none; }'
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('styled.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  customCSS: 'body { background: #f0f0f0; } .header { display: none; }'
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('styled.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]string{
        "url":       "https://example.com",
        "customCSS": "body { background: #f0f0f0; } .header { display: none; }",
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("styled.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Custom JavaScript
  customJS: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "customJS": "document.querySelector(\\".popup\\").remove();"
  }' \\
  --output modified.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://example.com',
    customJS: 'document.querySelector(".popup").remove();'
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('modified.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://example.com',
        'customJS': 'document.querySelector(".popup").remove();'
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('modified.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'customJS' => 'document.querySelector(".popup").remove();'
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('modified.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  customJS: 'document.querySelector(".popup").remove();'
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('modified.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]string{
        "url":      "https://example.com",
        "customJS": "document.querySelector(\\".popup\\").remove();",
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("modified.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // PDF Generation
  pdf: {
    curl: `curl ${baseURL}/api/pdf \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "format": "A4",
    "printBackground": true
  }' \\
  --output document.pdf`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/pdf',
  {
    url: 'https://example.com',
    format: 'A4',
    printBackground: true
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('document.pdf', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/pdf',
    json={
        'url': 'https://example.com',
        'format': 'A4',
        'printBackground': True
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('document.pdf', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'format' => 'A4',
    'printBackground' => true
];

$ch = curl_init('${baseURL}/api/pdf');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$pdfData = curl_exec($ch);
curl_close($ch);

file_put_contents('document.pdf', $pdfData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/pdf')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  format: 'A4',
  printBackground: true
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('document.pdf', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "url":             "https://example.com",
        "format":          "A4",
        "printBackground": true,
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/pdf", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("document.pdf")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Dark Mode
  darkmode: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "darkMode": true
  }' \\
  --output dark.png`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://example.com',
    darkMode: true
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('dark.png', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://example.com',
        'darkMode': True
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('dark.png', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'url' => 'https://example.com',
    'darkMode' => true
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('dark.png', $imageData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  darkMode: true
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('dark.png', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "url":      "https://example.com",
        "darkMode": true,
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("dark.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // PDF from HTML
  pdfHTML: {
    curl: `curl ${baseURL}/api/pdf \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "html": "<h1>Hello World</h1><p>PDF from HTML</p>",
    "format": "A4"
  }' \\
  --output document.pdf`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/pdf',
  {
    html: '<h1>Hello World</h1><p>PDF from HTML</p>',
    format: 'A4'
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('document.pdf', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/pdf',
    json={
        'html': '<h1>Hello World</h1><p>PDF from HTML</p>',
        'format': 'A4'
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('document.pdf', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$data = [
    'html' => '<h1>Hello World</h1><p>PDF from HTML</p>',
    'format' => 'A4'
];

$ch = curl_init('${baseURL}/api/pdf');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$pdfData = curl_exec($ch);
curl_close($ch);

file_put_contents('document.pdf', $pdfData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/pdf')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  html: '<h1>Hello World</h1><p>PDF from HTML</p>',
  format: 'A4'
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('document.pdf', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]string{
        "html":   "<h1>Hello World</h1><p>PDF from HTML</p>",
        "format": "A4",
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/pdf", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("document.pdf")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Error 400
  error400: {
    curl: `# 400 Bad Request Response
{
  "error": "Bad Request",
  "message": "Invalid URL provided",
  "statusCode": 400
}`,

    node: `// 400 Bad Request Response
{
  error: "Bad Request",
  message: "Invalid URL provided",
  statusCode: 400
}`,

    python: `# 400 Bad Request Response
{
  "error": "Bad Request",
  "message": "Invalid URL provided",
  "statusCode": 400
}`,

    php: `// 400 Bad Request Response
{
  "error": "Bad Request",
  "message": "Invalid URL provided",
  "statusCode": 400
}`,

    ruby: `# 400 Bad Request Response
{
  "error": "Bad Request",
  "message": "Invalid URL provided",
  "statusCode": 400
}`,

    go: `// 400 Bad Request Response
{
  "error": "Bad Request",
  "message": "Invalid URL provided",
  "statusCode": 400
}`
  },

  // Hide Elements
  hide: {
    curl: `curl ${baseURL}/api/screenshot \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "url": "https://example.com",
    "hideElements": [".ads", ".popup", "#cookie-banner"]
  }' \\
  --output clean.png`,
    
    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  {
    url: 'https://example.com',
    hideElements: ['.ads', '.popup', '#cookie-banner']
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('clean.png', response.data);`,
    
    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={
        'url': 'https://example.com',
        'hideElements': ['.ads', '.popup', '#cookie-banner']
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('clean.png', 'wb') as f:
    f.write(response.content)`,
    
    php: `<?php
$data = [
    'url' => 'https://example.com',
    'hideElements' => ['.ads', '.popup', '#cookie-banner']
];

$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('clean.png', $imageData);`,
    
    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  url: 'https://example.com',
  hideElements: ['.ads', '.popup', '#cookie-banner']
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('clean.png', response.body)`,
    
    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "url":          "https://example.com",
        "hideElements": []string{".ads", ".popup", "#cookie-banner"},
    }
    
    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    file, _ := os.Create("clean.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

  // Error 401
  error401: {
    curl: `# 401 Unauthorized Response
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key",
  "statusCode": 401
}`,
    
    node: `// 401 Unauthorized Response
{
  error: "Unauthorized",
  message: "Invalid or missing API key",
  statusCode: 401
}`,
    
    python: `# 401 Unauthorized Response
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key",
  "statusCode": 401
}`,
    
    php: `// 401 Unauthorized Response
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key",
  "statusCode": 401
}`,
    
    ruby: `# 401 Unauthorized Response
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key",
  "statusCode": 401
}`,
    
    go: `// 401 Unauthorized Response
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key",
  "statusCode": 401
}`
  },
    error429: {
    curl: `# 429 Too Many Requests Response
{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Try again later.",
  "statusCode": 429
}`,
    
    node: `// 429 Too Many Requests Response
{
  error: "Too Many Requests",
  message: "Rate limit exceeded. Try again later.",
  statusCode: 429
}`,
    
    python: `# 429 Too Many Requests Response
{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Try again later.",
  "statusCode": 429
}`,
    
    php: `// 429 Too Many Requests Response
{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Try again later.",
  "statusCode": 429
}`,
    
    ruby: `# 429 Too Many Requests Response
{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Try again later.",
  "statusCode": 429
}`,
    
    go: `// 429 Too Many Requests Response
{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Try again later.",
  "statusCode": 429
}`
},

  // Batch Processing
  batch: {
    curl: `curl ${baseURL}/api/screenshot/batch \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "urls": [
      "https://example.com",
      "https://google.com"
    ],
    "options": {
      "format": "png"
    }
  }'`,

    node: `const axios = require('axios');

const response = await axios.post(
  '${baseURL}/api/screenshot/batch',
  {
    urls: ['https://example.com', 'https://google.com'],
    options: { format: 'png' }
  },
  { headers: { 'X-API-Key': '${apiKey}' } }
);

// Save first image
const firstImage = response.data.images[0];
if (firstImage.success) {
  const fs = require('fs');
  const imageBuffer = Buffer.from(firstImage.base64, 'base64');
  fs.writeFileSync(firstImage.filename, imageBuffer);
}`,

    python: `import requests
import base64

response = requests.post(
    '${baseURL}/api/screenshot/batch',
    json={
        'urls': ['https://example.com', 'https://google.com'],
        'options': {'format': 'png'}
    },
    headers={'X-API-Key': '${apiKey}'}
)

data = response.json()
first_image = data['images'][0]
if first_image['success']:
    image_data = base64.b64decode(first_image['base64'])
    with open(first_image['filename'], 'wb') as f:
        f.write(image_data)`,

    php: `<?php
$response = curl_exec($ch);
$result = json_decode($response, true);
$firstImage = $result['images'][0];
if ($firstImage['success']) {
    $imageData = base64_decode($firstImage['base64']);
    file_put_contents($firstImage['filename'], $imageData);
}`,

    ruby: `data = JSON.parse(response.body)
first_image = data['images'][0]
if first_image['success']
  image_data = Base64.decode64(first_image['base64'])
  File.write(first_image['filename'], image_data)
end`,

    go: `var result BatchResponse
json.NewDecoder(resp.Body).Decode(&result)
if result.Images[0].Success {
    imageData, _ := base64.StdEncoding.DecodeString(result.Images[0].Base64)
    os.WriteFile(result.Images[0].Filename, imageData, 0644)
}`
  },

  batchResponse: {
    curl: `{
  "success": true,
  "total": 2,
  "successful": 2,
  "failed": 0,
  "images": [
    {
      "url": "https://example.com",
      "success": true,
      "base64": "iVBORw0KGgoAAAA...",
      "filename": "example-com-1699123456789.png",
      "format": "png",
      "size": 123456
    },
    {
      "url": "https://google.com",
      "success": true,
      "base64": "iVBORw0KGgoAAAA...",
      "filename": "google-com-1699123456790.png",
      "format": "png",
      "size": 45201
    }
  ]
}`,
    node: `{ success: true, total: 2, successful: 2, failed: 0, images: [...] }`,
    python: `{ "success": True, "total": 2, "successful": 2, "failed": 0, "images": [...] }`,
    php: `{ "success": true, "total": 2, "successful": 2, "failed": 0, "images": [...] }`,
    ruby: `{ "success" => true, "total" => 2, "successful" => 2, "failed" => 0, "images" => [...] }`,
    go: `{ "success": true, "total": 2, "successful": 2, "failed": 0, "images": [...] }`
  },

  batchAdvanced: {
    curl: `curl ${baseURL}/api/screenshot/batch \\
  -d '{
    "urls": ["https://example.com", "https://example.org"],
    "options": {
      "format": "webp",
      "quality": 85,
      "fullPage": true,
      "blockCookieBanners": true
    }
  }'`,
    node: `const response = await axios.post('${baseURL}/api/screenshot/batch', {
  urls: ['https://example.com', 'https://example.org'],
  options: { format: 'webp', quality: 85, fullPage: true }
});`,
    python: `response = requests.post('${baseURL}/api/screenshot/batch', json={
    'urls': ['https://example.com', 'https://example.org'],
    'options': {'format': 'webp', 'quality': 85, 'fullPage': True}
})`,
    php: `$data = ['urls' => [...], 'options' => ['format' => 'webp', 'quality' => 85]];`,
    ruby: `request.body = {urls: [...], options: {format: 'webp', quality: 85}}.to_json`,
    go: `data := map[string]interface{}{"urls": [...], "options": map[string]interface{}{"format": "webp"}}`
  },

  batchZip: {
    curl: `curl ${baseURL}/api/screenshot/batch?responseFormat=zip \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "urls": ["https://example.com", "https://google.com"],
    "options": { "format": "png" }
  }' \\
  --output screenshots.zip`,

    node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot/batch?responseFormat=zip',
  {
    urls: ['https://example.com', 'https://google.com'],
    options: { format: 'png' }
  },
  {
    headers: { 'X-API-Key': '${apiKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('screenshots.zip', response.data);`,

    python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot/batch?responseFormat=zip',
    json={
        'urls': ['https://example.com', 'https://google.com'],
        'options': {'format': 'png'}
    },
    headers={'X-API-Key': '${apiKey}'}
)

with open('screenshots.zip', 'wb') as f:
    f.write(response.content)`,

    php: `<?php
$ch = curl_init('${baseURL}/api/screenshot/batch?responseFormat=zip');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${apiKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'urls' => ['https://example.com', 'https://google.com'],
        'options' => ['format' => 'png']
    ]),
    CURLOPT_RETURNTRANSFER => true
]);

$zipData = curl_exec($ch);
curl_close($ch);

file_put_contents('screenshots.zip', $zipData);`,

    ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot/batch?responseFormat=zip')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${apiKey}'
request['Content-Type'] = 'application/json'
request.body = {
  urls: ['https://example.com', 'https://google.com'],
  options: {format: 'png'}
}.to_json

response = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) do |http|
  http.request(request)
end

File.write('screenshots.zip', response.body)`,

    go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    data := map[string]interface{}{
        "urls":    []string{"https://example.com", "https://google.com"},
        "options": map[string]string{"format": "png"},
    }

    body, _ := json.Marshal(data)
    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot/batch?responseFormat=zip", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${apiKey}")
    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()

    file, _ := os.Create("screenshots.zip")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
  },

// Response Success (add after error429)
  responseSuccess: {
    curl: `# 200 Success Response
{
  "success": true,
  "data": {
    "screenshotUrl": "https://cdn.snaptura.online/screenshots/abc123.png",
    "format": "png",
    "width": 1920,
    "height": 1080
  }
}`,
    
    node: `// 200 Success Response
{
  success: true,
  data: {
    screenshotUrl: "https://cdn.snaptura.online/screenshots/abc123.png",
    format: "png",
    width: 1920,
    height: 1080
  }
}`,
    
    python: `# 200 Success Response
{
  "success": True,
  "data": {
    "screenshotUrl": "https://cdn.snaptura.online/screenshots/abc123.png",
    "format": "png",
    "width": 1920,
    "height": 1080
  }
}`,
    
    php: `// 200 Success Response
{
  "success": true,
  "data": {
    "screenshotUrl": "https://cdn.snaptura.online/screenshots/abc123.png",
    "format": "png",
    "width": 1920,
    "height": 1080
  }
}`,
    
    ruby: `# 200 Success Response
{
  "success": true,
  "data": {
    "screenshotUrl": "https://cdn.snaptura.online/screenshots/abc123.png",
    "format": "png",
    "width": 1920,
    "height": 1080
  }
}`,
    
    go: `// 200 Success Response
{
  "success": true,
  "data": {
    "screenshotUrl": "https://cdn.snaptura.online/screenshots/abc123.png",
    "format": "png",
    "width": 1920,
    "height": 1080
  }
}`
  }
};

// Helper function to safely get code example with fallbacks
export const getCodeExample = (section, language) => {
  // Check if section exists
  if (!codeExamples[section]) {
    console.warn(`Section '${section}' not found in codeExamples`);
    return `// Code example not available for section: ${section}`;
  }

  // Check if language exists for this section
  if (!codeExamples[section][language]) {
    console.warn(`Language '${language}' not found for section '${section}'`);
    // Fallback to curl if available
    return codeExamples[section].curl || `// Code example not available for ${language} in ${section}`;
  }

  return codeExamples[section][language];
};

// Get all available sections
export const getAvailableSections = () => {
  return Object.keys(codeExamples);
};

// Get all available languages for a section
export const getAvailableLanguages = (section) => {
  if (!codeExamples[section]) {
    return [];
  }
  return Object.keys(codeExamples[section]);
};